import { Box } from '@mui/material'
import { GridColumns } from '@mui/x-data-grid-pro'
import { t as translate } from 'i18next'
import { Dispatch, FC, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useGetMoorings } from 'src/api/moorings'
import { EmptyStatusCard } from 'src/components/mooring/EmptyStatusCard'
import { CenteredSpinner } from 'src/components/Spinner'
import { renderCaptionedCell } from 'src/components/table/CaptionedCell'
import GenericTable from 'src/components/table/GenericTable'
import { PositionTableFilter } from 'src/screens/localities/moorings/PositionTableFilter'
import { Flex } from 'src/styles/flexComponent'
import { getMooringName, getPositionName } from 'src/utils/function'
import { useGetPositionsForDropDownByLocality } from '../../../api/positions'
import NoDeviationStepperContent from '../../../assets/svg/no-deviation-stepper-content.svg'
import { APIResultCount } from '../../../constant'
import { IMooring, ILocalityPositionDropDown, IPosition, IPositionType } from '../../../generated-types'
import MooringSelectCard from '../MooringSelectCard'

interface ISelectPositionStepProps {
  customerId: EntityId
  localityId: EntityId
  setDisabled: Dispatch<SetStateAction<boolean>>
  handleNext: () => void
}
const SelectPositionStep: FC<ISelectPositionStepProps> = ({ customerId, localityId, setDisabled, handleNext }) => {
  const { getValues, reset, setValue } = useFormContext()
  const [filteredTypes, setFilteredTypes] = useState<IPositionType[]>([])
  const [searchInput, setSearchInput] = useState('')
  const [mooringId, setMooringId] = useState<number>()
  const { data: positions, isLoading } = useGetPositionsForDropDownByLocality(customerId, localityId)
  const { data: moorings } = useGetMoorings(
    customerId,
    localityId,
    { excludeArchivedMoorings: true },
    0,
    APIResultCount.Max,
  )

  const filteredPositions = useMemo(() => {
    // Cages are excluded since we have seperate step for selecting cage positions
    let filtered = positions?.filter(p => p.type !== IPositionType.Cage) ?? []
    if (mooringId) {
      filtered = filtered.filter(p => p.mooringId === mooringId)
    }
    filtered = filteredTypes.length === 0 ? filtered : filtered.filter(p => filteredTypes.includes(p.type))
    return filtered.filter(
      p =>
        p.name.toLocaleLowerCase().includes(searchInput.toLocaleLowerCase()) ||
        p.mooringName?.toLocaleLowerCase().includes(searchInput.toLocaleLowerCase()) ||
        translate(IPositionType[p.type]).toLocaleLowerCase().includes(searchInput.toLocaleLowerCase()),
    )
  }, [filteredTypes, positions, searchInput, mooringId])

  const STATIC_COLUMNS: GridColumns = useMemo(
    () => [
      {
        field: 'name',
        headerName: translate('name', { ns: 'common' }),
        flex: 1,
        minWidth: 320,
        cellClassName: 'font-weight-semi-bold',
        filterable: true,
        renderCell: params => {
          return renderCaptionedCell(
            getPositionName(params.row),
            `${translate('mooring', { ns: 'common' })}: ${getMooringName(params.row.mooringName)} | ${translate(
              'type',
              { ns: 'common' },
            )}: ${translate(IPositionType[params.row.type], { ns: 'position' })}`,
          )
        },
      },
    ],
    [],
  )

  const columns: GridColumns = useMemo(() => [...STATIC_COLUMNS], [])

  useEffect(() => {
    if (getValues('position')?.id === undefined) {
      setDisabled(true)
    } else {
      setDisabled(false)
    }
  }, [getValues('position'), setDisabled])


  const handleRowClick = useCallback(
    params => {
      if (params.row.id !== getValues('position')?.id) {
        reset({
          ...getValues(),
          position: params.row as IPosition,
          mooring: { id: params.row.mooringId, name: params.row.mooringName } as IMooring,
          component: undefined,
          mainComponent: undefined,
        })
      } else {
        reset({
          ...getValues(),
          position: params.row as IPosition,
          mooring: { id: params.row.mooringId, name: params.row.mooringName } as IMooring,
        })
      }
      setDisabled(false)
      handleNext()
    },
    [handleNext, setDisabled],
  )

  return (
    <Box width={'100%'} sx={{ ml: '10px' }}>
      <Flex.Row overflow={'auto'} gap={2} padding={'8px'}>
        {moorings?.items.map(mooring => (
          <MooringSelectCard
            key={mooring.id}
            mooring={mooring}
            onClick={() => {
              setMooringId(mooring.id)
              setValue('mooringId', mooring.id)
              setValue('mooring', { id: mooring.id, name: mooring.name } as IMooring)
            }}
            isSelected={mooring?.id == mooringId}
          />
        ))}
      </Flex.Row>
      {isLoading ? (
        <Box width={'100%'} height={'425px'} display="flex" justifyContent="center" alignItems="center">
          <CenteredSpinner />
        </Box>
      ) : positions && positions?.length != 0 ? (
        <Box width={'100%'} py={'16px'}>
          <PositionTableFilter
            setFilteredTypes={setFilteredTypes}
            filteredTypes={filteredTypes}
            setSearchInput={setSearchInput}
            searchInput={searchInput}
          />
          <GenericTable
            columns={columns}
            data={[...([...(filteredPositions || [])]?.sort(x => parseInt(x.name))?.reverse() || [])] as ILocalityPositionDropDown[]}
            enableToolbar={true}
            height={350}
            enableToolbarFilter={true}
            loading={isLoading}
            onRowClick={handleRowClick}
            selectionModel={getValues('position') ? [getValues('position').id] : []}
            id={'create-deviation-positions'}
            autoHeight={false}
            getRowHeight={() => 72}
            minRowHeight="72px !important"
            sortModel={[{ field: 'name', sort: 'asc' }]}
          />
        </Box>
      ) : (
        <Box className="text-center">
          <EmptyStatusCard
            image={NoDeviationStepperContent}
            header={translate('deviation-position-empty-title', { ns: 'deviation' })}
            content={translate('deviation-position-empty-content', { ns: 'deviation' })}
            imageWidth="89px"
            headerFontSize="18px"
            contentFontSize="14px"
            contentClasses="mt-8"
            imageContainerClasses="p-4"
            contentTopMargin="10px"
          />
        </Box>
      )}
    </Box>
  )
}

export default SelectPositionStep
